import styled from "@emotion/styled";
import { SPACE_MONO } from "@stepfinance/react";
import { useQueryClient } from "@tanstack/react-query";
import type { FC } from "react";
import { useMemo } from "react";
import { useState } from "react";
import { useEffect } from "react";

import { useLanguageContext } from "../../contexts/language/language";
import { useTransactionCacheStatus } from "../../hooks/queries/transactionHistory/useTransactionCacheStatus";
import type { ModalProps } from "../Modal/Modal";
import { Modal } from "../Modal/Modal";
import { TextDescription } from "../shared";
import { StepProgress } from "../StepProgress/StepProgress";

interface LoadingTransactionsModalProps
  extends Omit<ModalProps, "title" | "children" | "TitleIcon" | "onClose" | "visible"> {
  yearLoading: number | string;
}

export const LoadingTransactionsModal: FC<LoadingTransactionsModalProps> = ({ yearLoading, ...props }) => {
  const cacheStatusQuery = useTransactionCacheStatus(true);
  const [progress, setProgress] = useState(0);
  const [currentYearLoading, setCurrentYearLoading] = useState(yearLoading);
  const [visible, setVisible] = useState(false);
  const oldestTransactionLoaded = useMemo(
    () =>
      cacheStatusQuery.data?.oldestCachedDateTime ? new Date(cacheStatusQuery.data.oldestCachedDateTime) : new Date(),
    [cacheStatusQuery.data?.oldestCachedDateTime],
  );

  const {
    currentLanguageData: {
      components: { loadingTransactionsModal },
    },
  } = useLanguageContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    const isCurrentYear = currentYearLoading === oldestTransactionLoaded.getFullYear();
    const cacheData = cacheStatusQuery.data;
    const isScanComplete = cacheData?.initialScanComplete;
    if (cacheData && isCurrentYear && !isScanComplete) {
      setVisible(true);
    }
    const firstDayOfYear = new Date(Number(currentYearLoading), 0, 1).getTime();
    const lastDayOfYear = isCurrentYear
      ? new Date().getTime()
      : new Date(Number(currentYearLoading) + 1, 0, 1).getTime();

    const yearProgress = ((lastDayOfYear - oldestTransactionLoaded.getTime()) / (lastDayOfYear - firstDayOfYear)) * 100;
    const scanProgress = isScanComplete ? 100 : getPercentageRange(yearProgress);

    setProgress(scanProgress);
    if (yearProgress >= 100) {
      setCurrentYearLoading(oldestTransactionLoaded.getFullYear());
      setVisible(false);
      void queryClient.invalidateQueries({ queryKey: ["tx-history"] });
    }
    if (isScanComplete || !isCurrentYear) {
      setVisible(false);
    }
  }, [cacheStatusQuery.data, currentYearLoading, oldestTransactionLoaded, queryClient]);

  const getPercentageRange = (percentage: number) => {
    const MIN = 0;
    const MAX = 100;
    return Math.floor(Math.min(Math.max(percentage, MIN), MAX));
  };

  return (
    <Modal {...props} visible={visible} title={"We're loading your transactions"} onClose={() => setVisible(false)}>
      <ContentContainer>
        <TextDescription className="description">{loadingTransactionsModal.description}</TextDescription>
        <div className="progress-container">
          <StepProgress
            percent={progress}
            showInfo
            type="line"
            legend={currentYearLoading}
            legendStyle={{ fontFamily: SPACE_MONO }}
          />
        </div>
      </ContentContainer>
    </Modal>
  );
};

const ContentContainer = styled.div`
  .description {
    margin-bottom: 24px;
  }
  .progress-container {
    width: 85%;
    margin: auto;
    margin-bottom: 18px;
  }
`;
